import React from 'react';
import { Collapse, Typography, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Markdown from 'markdown-to-jsx';
import markdownOverrides from '../../utils/MarkdownOverrides';
// src
import { NextImage } from '../NextImage/index';
// styles
import styles from './styles';

export interface LibCardFoxtonsPrimeTeamProps {
  isDetailOpen?: boolean;
  memberImg: {
    image: string;
    alt?: string;
  };
  name: string;
  subtitle: string;
  description: string;
  email?: string;
  emailLink?: string;
  chat?: string;
  weChatLink?: string;
  expandedStateOptions?: {
    setExpandedState: (val: boolean) => void;
    expandedState: boolean;
  };
}

const getInverseExpandedState = (
  expandedStateExternal: boolean | undefined,
  expandedStateInternal: boolean
) =>
  expandedStateExternal !== undefined
    ? !expandedStateExternal
    : !expandedStateInternal;

function LibCardFoxtonsPrimeTeam({
  isDetailOpen = false,
  memberImg,
  name,
  subtitle,
  description,
  email = 'email',
  emailLink = 'N/A',
  chat = 'Wechat',
  weChatLink = 'N/A',
  expandedStateOptions
}: LibCardFoxtonsPrimeTeamProps) {
  const [expanded, setExpanded] = React.useState(isDetailOpen);
  const { expandedState, setExpandedState } = expandedStateOptions;

  const isExpandable = !!description;

  const handleExpandClick = () => {
    if (!isExpandable) {
      return;
    }

    if (setExpandedState) {
      setExpandedState(!expandedState);
    } else {
      setExpanded(!expanded);
    }
  };

  const overrides = {
    ...markdownOverrides,
    p: {
      component: Typography,
      props: {
        variant: 'body1',
        mb: 0.5
      }
    }
  };

  return (
    <Box sx={styles.cardMainBox}>
      <Box sx={styles.mainImgBox}>
        <NextImage
          image={{ src: memberImg?.image, alt: memberImg?.alt }}
          style={{ width: '100%', height: '20rem', objectFit: 'cover' }}
          sizes="80vw"
        />
      </Box>
      <Box
        component="button"
        onClick={handleExpandClick}
        sx={[
          styles.cardActionArea,
          isExpandable && styles.clickableCardActionArea
        ]}
      >
        <Box>
          <Typography variant="h3" sx={styles.nameTitle}>
            {name}
          </Typography>
          <Typography variant="body1" sx={styles.subtitle}>
            {subtitle}
          </Typography>
        </Box>
        {isExpandable && (
          <Box
            sx={{
              transform: getInverseExpandedState(expandedState, expanded)
                ? 'rotate(0deg)'
                : 'rotate(180deg)',
              ...styles.expandIconBox
            }}
          >
            <ExpandMoreIcon />
          </Box>
        )}
      </Box>

      {/* Collapsible Section */}
      <Collapse in={expandedState ?? expanded} timeout="auto" unmountOnExit>
        <Box sx={styles.detailBox}>
          {/* Long Description */}
          <Box sx={styles.descriptionText}>
            <Markdown
              className="markdown"
              options={{
                overrides,
                wrapper: 'div',
                forceWrapper: true,
                forceBlock: true
              }}
            >
              {description}
            </Markdown>
          </Box>

          {/* Contact Box */}
          <Box sx={styles.contactBox}>
            <Box sx={styles.contactBoxText}>
              {email && (
                <Typography sx={styles.contactText}>
                  {email}: <a href={`mailto:${emailLink}`}>{emailLink}</a>
                </Typography>
              )}

              {weChatLink && (
                <Typography sx={styles.contactTextWechat}>
                  {chat}: {weChatLink}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export const CardFoxtonsPrimeTeam = React.memo(LibCardFoxtonsPrimeTeam);

export default CardFoxtonsPrimeTeam;
